import { DataGrid } from "@mui/x-data-grid";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

const XmlItemsGrid = ({ rows = [], selectedId, onSelect, onRowsChange, readOnly = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleCheckboxChange = (id) => {
    if (String(selectedId) === String(id)) {
      onSelect(null);
      return;
    }
    onSelect(id);
  };

  const handleProcessRowUpdate = (newRow) => {
    if (!onRowsChange) return newRow;

    const updatedRows = rows.map((row) =>
      String(row.id) === String(newRow.id) ? newRow : row,
    );

    onRowsChange(updatedRows);
    return newRow;
  };

  const selectColumn = !readOnly
    ? [{
        field: "select",
        headerName: "",
        width: 55,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Checkbox
            size="small"
            checked={String(selectedId) === String(params.id)}
            onChange={() => handleCheckboxChange(params.id)}
          />
        ),
      }]
    : [];

  const columns = [
    ...selectColumn,
    { field: "item_nf", headerName: t("assing.item"), width: 110 },
    { field: "material_xml", headerName: t("assing.materialXml"), width: 190 },
    { field: "description_material_xml", headerName: t("assing.descMaterialXml"), width: 260 },
    { field: "qty", headerName: t("assing.qty"), width: 150 },
    { field: "uom_xml", headerName: t("assing.unit"), width: 130 },
    { field: "ncm_xml", headerName: t("simulate.ncm"), width: 150 },
    {
      field: "purchase_order",
      headerName: t("assing.purchaseOrder"),
      width: 140,
      editable: !readOnly,
      cellClassName: readOnly ? undefined : "editable-cell",
    },
    {
      field: "order_item",
      headerName: t("assing.purchaseItem"),
      width: 100,
      editable: !readOnly,
      cellClassName: readOnly ? undefined : "editable-cell",
    },
    {
      field: "tax_code",
      headerName: t("simulate.iva"),
      width: 100,
      editable: !readOnly,
      cellClassName: readOnly ? undefined : "editable-cell",
    },
    {
      field: "cfop",
      headerName: t("simulate.cfop"),
      width: 100,
      editable: !readOnly,
      cellClassName: readOnly ? undefined : "editable-cell",
    },
    {
      field: "uom_erp",
      headerName: t("assing.unitErp"),
      width: 190,
      sortable: false,
      filterable: false,
      cellClassName: readOnly ? undefined : "editable-cell",
      renderCell: (params) => {
        const val = params.row.uom_erp;

        if (readOnly) {
          return (
            <span style={{ fontSize: "inherit", color: val ? "inherit" : theme.palette.text.secondary, paddingLeft: 8 }}>
              {val || "—"}
            </span>
          );
        }

        const options = params.row?.uomOptions ?? [];

        if (options.length === 0) {
          return (
            <span style={{ fontSize: "inherit", color: val ? "inherit" : theme.palette.text.secondary, paddingLeft: 8 }}>
              {val || "—"}
            </span>
          );
        }

        return (
          <TextField
            select
            size="small"
            value={val ?? ""}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              const newValue = e.target.value;
              const updatedRows = rows.map((row) =>
                String(row.id) === String(params.id)
                  ? { ...row, uom_erp: newValue }
                  : row,
              );
              onRowsChange?.(updatedRows);
            }}
            variant="standard"
            sx={{
              width: "100%",
              "& .MuiInputBase-root": { fontSize: "inherit" },
              "& .MuiInput-root:before": { borderBottom: "none !important" },
              "& .MuiInput-root:after": { borderBottom: "none !important" },
              "& .MuiInput-root:hover:not(.Mui-disabled):before": { borderBottom: "none !important" },
              "& .MuiSelect-select": { padding: "0 8px", lineHeight: "36px" },
              "& .MuiSelect-icon": { right: 4 },
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      },
    },
  ];

  return (
    <div style={{ height: "100%", width: "100%", overflow: "auto" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        density="compact"
        hideFooter
        disableRowSelectionOnClick
        onRowClick={readOnly ? undefined : (params) => onSelect(params.id)}
        onCellDoubleClick={readOnly ? undefined : (params) => {
          if (params.field === "uom_erp") return;
        }}
        isCellEditable={readOnly ? () => false : undefined}
        processRowUpdate={handleProcessRowUpdate}
        getRowClassName={(params) => {
          if (params.row.assigned) {
            return "row-status-success";
          }
          return "row-status-error";
        }}
        sx={{
          border: 0,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "background.subtle",
            borderBottom: "2px solid",
            borderColor: "divider",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            fontSize: "0.7rem",
            color: "text.secondary",
            textTransform: "uppercase",
          },
          "& .editable-cell": {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            cursor: "pointer",
          },
          "& .grid-row-selected": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          "& .row-status-success .MuiDataGrid-cell:first-of-type": {
            borderLeft: "4px solid #22c55e",
          },
          "& .row-status-error .MuiDataGrid-cell:first-of-type": {
            borderLeft: "4px solid #ef4444",
          },
        }}
      />
    </div>
  );
};

export default XmlItemsGrid;